import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lightbulb, Loader2, Copy, Sparkles, FileText, Save, Check, MessageCircle } from 'lucide-react';
import { Sidebar } from '../../components/Sidebar';
import { CreatorPicker } from './CreatorPicker';
import { TrendingGrid } from './TrendingGrid';
import { IdeaCard } from './IdeaCard';
import { MultiSelect } from '../ideation/MultiSelect';
import { SavedPacks } from './SavedPacks';
import { ContextField } from './ContextField';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const NICHES = ['Food', 'Comedy', 'Lifestyle', 'Fashion', 'Beauty', 'Travel', 'Entertainment', 'Fitness', 'Finance', 'Tech'];
const LABEL = 'text-xs font-semibold uppercase tracking-[0.08em] block mb-2 text-[#0F0F0F] dark:text-white';

const Pills = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(value === o ? '' : o)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${value === o ? 'bg-[#FF451A] border-[#FF451A] text-white' : 'bg-white dark:bg-[#1A1D24] border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white hover:border-[#FF451A]/50'}`} data-testid={`${testid}-${o.toLowerCase()}`}>
        {o}
      </button>
    ))}
  </div>
);

const ideasToText = (title, ideas) => [
  `💡 ${title}`,
  ...ideas.map((i, n) => `${n + 1}. ${i.title || `Idea ${n + 1}`}\nHook: ${i.hook}\nShoot: ${i.shoot || i.description || ''}\nTags: ${(i.hashtags || []).join(' ')}\nAudio: ${i.audio}`),
].join('\n\n');

const BTN = 'rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 border border-black/[0.12] dark:border-white/[0.08] bg-white dark:bg-[#1A1D24] text-[#0F0F0F] dark:text-white hover:border-[#FF451A]/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const opts = { withCredentials: true };

const SourceLabel = ({ source, aiAvailable }) => (
  <span className="text-[11px] inline-flex items-center gap-1 text-[#6B6B6B] dark:text-zinc-400" data-testid="creator-ideation-ideas-source">
    {source === 'ai' ? <><Sparkles size={11} className="text-[#FF451A]" /> AI-generated from trending reels</> : <><FileText size={11} /> {aiAvailable ? 'Template ideas' : 'Template ideas — add AI key for personalized ideas'}</>}
  </span>
);

export default function CreatorIdeation() {
  const [creator, setCreator] = useState(null);
  const [niches, setNiches] = useState([]);
  const [language, setLanguage] = useState('Hinglish');
  const [format, setFormat] = useState('Reel');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [packs, setPacks] = useState([]);
  const [packId, setPackId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regen, setRegen] = useState(null);
  const [context, setContext] = useState('');
  const [ctxOpen, setCtxOpen] = useState(false);
  const [ctxSaved, setCtxSaved] = useState(false);

  const pickCreator = (c) => {
    setCreator(c);
    if (c?.niche && !niches.length) setNiches([c.niche]);
    setContext(c?.ideation_context || '');
    setCtxSaved(!!c?.ideation_context);
    setCtxOpen(!!c?.ideation_context);
  };

  const loadPacks = useCallback(() => {
    axios.get(`${API_URL}/api/creator-ideation/packs`, opts).then(({ data }) => setPacks(data.packs)).catch(() => {});
  }, []);
  useEffect(loadPacks, [loadPacks]);

  const submit = async (e) => {
    e.preventDefault();
    if (!niches.length) { toast.error('Pick at least one niche'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/creator-ideation/generate`, { creator_id: creator?.id || null, niche: niches, language, format, creator_context: context }, { withCredentials: true });
      setResult(data);
      setPackId(null);
      if (creator) setCreator((c) => ({ ...c, ideation_context: data.creator_context }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not get ideas');
    } finally {
      setLoading(false);
    }
  };

  const savePack = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/creator-ideation/packs`, {
        creator_id: result.creator?.id || null, creator_name: result.creator?.name || null, niches: result.niches,
        format: result.format, language: result.language, ideas: result.ideas, ideas_source: result.ideas_source, creator_context: result.creator_context || null,
      }, opts);
      setPackId(data.id);
      toast.success(`Pack saved · ${data.idea_count} ideas`);
      loadPacks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save pack');
    } finally {
      setSaving(false);
    }
  };

  const openPack = async (id) => {
    try {
      const { data: p } = await axios.get(`${API_URL}/api/creator-ideation/packs/${id}`, opts);
      setResult({
        niche: p.niches.join(' + '), niches: p.niches, format: p.format, language: p.language, ideas: p.ideas,
        ideas_source: p.ideas_source, ai_available: true, trending: null, creator_context: p.creator_context || '', fromPack: true,
        creator: p.creator_id ? { id: p.creator_id, name: p.creator_name, phone: p.creator_phone } : null,
      });
      setPackId(id);
      window.scrollTo({ top: document.querySelector('[data-testid="creator-ideation-saved-packs"]')?.offsetTop || 0, behavior: 'smooth' });
    } catch {
      toast.error('Could not open pack');
    }
  };

  const regenerate = async (index) => {
    setRegen(index);
    try {
      const { data } = await axios.post(`${API_URL}/api/creator-ideation/regenerate-one`, {
        creator_id: result.creator?.id || null, niche: result.niches, format: result.format, language: result.language,
        creator_context: result.creator_context ?? '', idea_index: index, existing_ideas: result.ideas.map((i) => i.title || i.hook),
      }, opts);
      setResult((r) => ({ ...r, ideas: r.ideas.map((x, i) => (i === index ? data.idea : x)) }));
      setPackId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not regenerate this idea');
    } finally {
      setRegen(null);
    }
  };

  const ideasTitle = result ? `Content Ideas${result.creator ? ` for ${result.creator.name}` : ''} — ${result.niche} ${result.format}` : '';
  const copy = async () => {
    await navigator.clipboard.writeText(ideasToText(ideasTitle, result.ideas));
    toast.success(`All ${result.ideas.length} ideas copied — paste into WhatsApp`);
  };

  const phone = result?.creator?.phone;
  const sendWhatsApp = () => window.open(`https://wa.me/${phone}?text=${encodeURIComponent(ideasToText(ideasTitle, result.ideas))}`, '_blank', 'noopener');

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D] min-w-0" data-testid="creator-ideation-page">
        <div className="max-w-[1400px] mx-auto space-y-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3"><Lightbulb className="text-[#FF451A]" size={30} /> Ideation</h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Trending Instagram content and ready-to-shoot hooks to share with your creators.</p>
          </div>

          <form onSubmit={submit} className="bg-white dark:bg-[#0F1115] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6 space-y-5" data-testid="creator-ideation-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={LABEL}>Creator (optional)</label>
                <CreatorPicker value={creator} onChange={pickCreator} />
              </div>
              <div>
                <label className={LABEL}>Niches * {creator ? '(auto from creator, add more if needed)' : ''}</label>
                <MultiSelect value={niches} onChange={setNiches} options={NICHES} placeholder="Pick one or more niches" clearLabel="Clear all" testid="creator-ideation-niche-select" />
              </div>
            </div>
            <ContextField value={context} onChange={(v) => { setContext(v); setCtxSaved(false); }} open={ctxOpen} setOpen={setCtxOpen} saved={ctxSaved} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div><label className={LABEL}>Language</label><Pills options={['Hindi', 'English', 'Hinglish']} value={language} onChange={(v) => setLanguage(v || 'Hinglish')} testid="creator-ideation-lang" /></div>
              <div><label className={LABEL}>Format</label><Pills options={['Reel', 'Carousel', 'Story']} value={format} onChange={(v) => setFormat(v || 'Reel')} testid="creator-ideation-format" /></div>
            </div>
            <button type="submit" disabled={loading} className="rounded-full px-6 py-2.5 bg-[#FF451A] hover:bg-[#E63E17] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 transition-colors" data-testid="creator-ideation-submit-btn">
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Lightbulb size={15} />} {loading ? 'Generating…' : 'Get Ideas'}
            </button>
          </form>

          {loading && (
            <div className="flex items-center gap-3 rounded-2xl border border-dashed border-[#FF451A]/40 bg-[#FF451A]/[0.04] px-5 py-4 text-sm text-[#0F0F0F] dark:text-white" data-testid="creator-ideation-loading">
              <Loader2 size={16} className="animate-spin text-[#FF451A]" /> Analyzing trends and generating ideas for {creator?.name || niches.join(' + ')}…
            </div>
          )}

          <SavedPacks packs={packs} onOpen={openPack} activeId={packId} />

          {result && !loading && (
            <>
              {result.trending && <TrendingGrid niche={result.niche} posts={result.trending} error={result.trending_error} />}
              <section data-testid="creator-ideation-ideas">
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div>
                    <h2 className="text-lg font-semibold text-[#0F0F0F] dark:text-white" data-testid="creator-ideation-ideas-title">{ideasTitle}</h2>
                    <SourceLabel source={result.ideas_source} aiAvailable={result.ai_available} />
                    {result.fromPack && result.creator_context && (
                      <p className="mt-1 text-xs text-[#4B5563] dark:text-zinc-300 max-w-2xl" data-testid="creator-ideation-pack-context"><span className="font-semibold">Brief used:</span> {result.creator_context}</p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={copy} className={BTN} data-testid="creator-ideation-copy-btn">
                      <Copy size={13} /> Copy All {result.ideas.length} Ideas
                    </button>
                    {result.creator && (
                      <span title={phone ? `Open WhatsApp chat with ${result.creator.name}` : 'No phone number on file'} data-testid="creator-ideation-whatsapp-wrap">
                        <button onClick={sendWhatsApp} disabled={!phone} className={`${BTN} ${phone ? '!bg-[#25D366] !border-[#25D366] !text-white hover:!bg-[#1EBE5A]' : ''}`} data-testid="creator-ideation-whatsapp-btn">
                          <MessageCircle size={13} /> Send on WhatsApp
                        </button>
                      </span>
                    )}
                    <button onClick={savePack} disabled={saving || !!packId} className="rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 bg-[#FF451A] hover:bg-[#E63E17] text-white disabled:opacity-50 transition-colors" data-testid="creator-ideation-save-pack-btn">
                      {saving ? <Loader2 size={13} className="animate-spin" /> : packId ? <Check size={13} /> : <Save size={13} />} {packId ? 'Saved' : 'Save Pack'}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {result.ideas.map((idea, n) => <IdeaCard key={n} idea={idea} n={n} regenerating={regen === n} onRegenerate={result.ideas_source === 'ai' ? () => regen === null && regenerate(n) : undefined} />)}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
